import { gqlFetcher } from '#features/api';

/**
 * Переход по размеченной ссылке — первый шаг воронки «увидел ссылку → зарегистрировался».
 *
 * Считаем только тех, кто пришёл без аккаунта: это и есть кандидаты в регистрацию. Заодно так не
 * задваивается кнопка бота «войти через Discord»: её нажатие видит сервис входа, а сюда человек
 * возвращается уже с сессией и не считается второй раз.
 *
 * Идентификатора посетителя нет и не будет: сервер получает только метку и путь, это счётчик
 * переходов, а не слежка за человеком.
 */

// Строкой, а не через graphql(): мутация возвращает голый Boolean, выбирать нечего — тот же приём,
// что у телеметрии звонков.
const landingMutation = `
  mutation AcquisitionLanding(
    $source: String!
    $medium: String
    $campaign: String
    $content: String
    $path: String
  ) {
    acquisition {
      landing(source: $source, medium: $medium, campaign: $campaign, content: $content, path: $path)
    }
  }
`;

export type TaggedLanding = {
  source: string;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  path: string;
};

/** Метка из адреса страницы. Нет utm_source — это не переход по нашей ссылке. */
export const taggedLandingFrom = (search: string, pathname: string): TaggedLanding | null => {
  const params = new URLSearchParams(search);
  const source = params.get('utm_source')?.trim();

  if (!source) {
    return null;
  }

  return {
    source,
    medium: params.get('utm_medium'),
    campaign: params.get('utm_campaign'),
    content: params.get('utm_content'),
    // Только путь: в параметрах бывают одноразовые коды подтверждения и приглашений.
    path: pathname,
  };
};

/**
 * Ключ защёлки: одна и та же ссылка в одной вкладке считается один раз. Иначе каждое обновление
 * страницы добавляло бы клик, которого человек не делал.
 */
const onceKey = (landing: TaggedLanding) =>
  `tp_landing:${landing.path}|${landing.source}|${landing.campaign ?? ''}|${landing.content ?? ''}`;

const alreadyReported = (key: string) => {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    // Приватный режим бросает на само обращение к хранилищу. Лучше лишний клик, чем пропущенный.
    return false;
  }
};

const markReported = (key: string) => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // см. alreadyReported
  }
};

/** Fire-and-forget: счётчик не стоит ни ошибки в консоли, ни записи в Sentry. */
export const reportTaggedLanding = async (landing: TaggedLanding): Promise<void> => {
  const key = onceKey(landing);

  if (alreadyReported(key)) {
    return;
  }
  markReported(key);

  try {
    await gqlFetcher(landingMutation, landing, { silent: true });
  } catch {
    // Молча: см. комментарий к функции.
  }
};
